/* eslint-disable no-unused-vars */
import {Subject, BehaviorSubject, of, merge, empty} from 'rxjs';
import {filter, map, switchMap, mergeMap, take, tap, scan, takeUntil, pluck} from 'rxjs/operators';

let baseId = 0;


/*
{
  func: Function,
  events: [],
  cacheCount: 10,
  formatResult: false,
  itemCache: false,
  getKeyFromEvent: Function,
  getKeyFromArgs: Function,
  $bus: rootVue
}
 */
export class RXJSCache {
  constructor(config = {}) {
    this._config = config;
    baseId += 1;
    this._name = config.name || `RXJSCache@${baseId}`;
    this.$bus = config.$bus;
    this._func = config.func;
    this._m_events = config.events || []
    this._cacheCount = config.cacheCount || 10
    this._registerEvents()
    this._argsKeyCt = {}
    this._formatResult = config.formatResult
    // can load data from storage
    this._state$ = new BehaviorSubject(Object.assign({running: [], cache: {}, keys: []}));
    this._pbs$ = new Subject();
    this._init(this._pbs$, this._state$)
  }
  get name() {
    return this._name;
  }

  _init(pbs$, state$) {
    pbs$
      .pipe(
        map(this._runningKeysUpdate.bind(this)),
        filter(x => x.length),
        mergeMap(paramsList => merge(...paramsList.map(params => this._func(...params[0]).then(res => [params[1], res])))),
        map(resp => Object.assign({}, state$.value, {
          cache: {...state$.value.cache, [resp[0]]: resp[1]},
          running: state$.value.running.filter(x => x !== resp[0])
        })),
        // tap(res => console.debug('pbs$: state', res)) // can save to storage
      ).subscribe(state$)
  }
  _runningKeysUpdate(paramsList) {
    const state = this._state$.value;
    let cache = state.cache;
    let keys = state.keys;
    let running = state.running;

    const paramsKeys = paramsList.map(x => x[1]);
    const newKeys = paramsKeys.filter(x => keys.indexOf(x) === -1)
    const removeKeysCount = keys.length + newKeys.length - this._cacheCount;
    const shouldDelete = removeKeysCount > 0
      ? keys
        .filter(x => running.indexOf(x) === -1 && paramsKeys.indexOf(x) === -1)
        .slice(0, removeKeysCount)
      : [];
    shouldDelete.forEach(x => delete cache[x])
    keys = keys.filter(x => shouldDelete.indexOf(x) === -1)
    this._state$.next(Object.assign({}, state, {keys: [...keys, ...newKeys], cache: cache, running: [...running, ...newKeys]}))
    return paramsList.filter(x => newKeys.indexOf(x[1]) !== -1)
  }

  isHit(...args) {
    return !!(this._state$.value.cache[this.getKey(args)])
  }
  
  _registerEvents() {
    if (!this._m_events || !this._m_events.length) {
      return
    }
    console.debug('cache', this._m_events, ' register:', this._m_events)
    this.$bus.$on(this._m_events, this._clear.bind(this))
  }

  _clear(data) {
    console.debug('cache', this._m_events, '_clear')

    const state = this._state$.value;
    if(this._config.itemCache) {
      const eventKey = this._config.getKeyFromEvent(data)
      if (this._argsKeyCt[eventKey]) {
        const key = this._argsKeyCt[eventKey];
        if(state.running.indexOf(key) !== -1) {
          return
        }
        this._state$.next(Object.assign({}, state,{
          keys: state.keys.filter(x => x !== key),
          cache: {...state.cache, [key]: undefined}
        }))
        delete this._argsKeyCt[eventKey]
      }
    } else {
      this._state$.next({running: state.running, keys: state.running, cache: {}})
    }
  }

  watch$(...args) {
    return of(this.getKey(args)).pipe(
      tap(key => this._config.itemCache ? this._argsKeyCt[this._config.getKeyFromArgs(...args)] = key : ''),
      mergeMap(key => this._state$.pipe(
        pluck('cache', key),
        tap(x => !x && this._state$.value.running.indexOf(key) === -1? this._pbs$.next([[args, key]]) : '')) // Automatically refreshes when a subscription is available
      ),
      scan((y, x) => [x, x && x !== y[0]], [undefined, false]),
      filter(x => x[1]),
      map(x => x[0])
    )
  }

  getWatcher(stop$ = empty()) {
    const sub$ = new Subject()
    return {
      $: sub$.pipe(
        switchMap(args => this.watch$(...args)),
        takeUntil(stop$)
      ),
      next: (...args) => {
        sub$.next(args);
        return this.isHit(...args)
      },
    }
  }
  getData(...args) {
    const data$ = this.watch$(...args).pipe(take(1)).toPromise()
    return this._formatResult ? {isHit: false, data: data$} : data$
  }
  autoRegisterMixin() {
    const that = this;
    function getProxy(vc) {
      return {
        watch$: (...args) => that.watch$(...args).pipe(takeUntil(vc[`_${that.name}_destroy$`])),
        getWatcher: () => that.getWatcher(vc[`_${that.name}_destroy$`]),
        getData: (...args) => that.getData(...args)
      }
    }
    return {
      mounted() {
        this[`_${that.name}_destroy$`] = new Subject();
        this[that.name] = getProxy(this)
      },
      destroyed() {
        this[`_${that.name}_destroy$`].next(true);
      }
    }
  }
  getKey(args, keys, oKey) {
    const ks = keys || [];
    if (!args && args !== 0) {
      return '-'
    }
    if (Array.isArray(args)) {
      ks.push('<')
      args.forEach(i1 => this.getKey(i1, ks))
      ks.push('>')
    }
    else if (args.constructor === Object) {
      ks.push('<')
      Object.keys(args).forEach(oK => {
        ks.push(`${oK}@${this.getKey(args[oK])}@`)
      })
      ks.push('>')
    } else {
      if (oKey) {
        ks.push(`${oKey}-${args}`)
      } else {
        ks.push(`${args}`)
      }
    }
    if (!keys) {
      return ks.join('|')
    }
  }
}


// export const RXJSCache = _RXJSCache
